/**
 * CAO points for the Irish Leaving Certificate.
 *
 * Sources (retrieved October 2026):
 * - CAO, "New Common Points Scale for Entry to Higher Education from 2017":
 *   https://www2.cao.ie/downloads/documents/CommonPointsScale2017.pdf — Higher Level
 *   H1 100, H2 88, H3 77, H4 66, H5 56, H6 46, H7 37, H8 0; Ordinary Level O1 56, O2 46,
 *   O3 37, O4 28, O5 20, O6 12, O7 0, O8 0; 25 bonus points for Higher Level Mathematics at
 *   H6 or above; LCVP Distinction 66, Merit 46, Pass 28; and the conversion of 1992–2016
 *   grades (A1–NG) to the same scale.
 * - CAO, "Bonus Points for Higher Level Leaving Certificate Mathematics":
 *   https://www2.cao.ie/app_scoring/BonusPointsMathsWithExamples.pdf — the six highest scores
 *   in a single sitting are added; the bonus is added to the Mathematics score before the
 *   best six are chosen, so it only matters when Mathematics makes the six; for 1992–2016
 *   sittings the bonus applies from HD3 (the old D3) upwards. Its four worked examples are
 *   pinned in the tests.
 * - That LCVP Link Modules points may fill one of the best six places: Maynooth University,
 *   "Minimum entry requirements" (2022), and Shannon College of Hotel Management, "How to
 *   Make an Application".
 *
 * Not modelled: the reduced points scale CAO applies to medicine, the separate rules for
 * QQI-FET awards, and Foundation Level subjects, which the common scale does not score.
 */

export type CaoScheme = "current" | "legacy";
export type CaoLevel = "higher" | "ordinary";
export type LcvpGrade = "distinction" | "merit" | "pass";

export const CAO_MATHS_BONUS = 25;
export const CAO_COUNTED_SUBJECTS = 6;
/** Six H1s plus the 25-point Mathematics bonus: 6 × 100 + 25. */
export const CAO_MAX_POINTS = 625;

const CURRENT_HIGHER = {
  H1: 100,
  H2: 88,
  H3: 77,
  H4: 66,
  H5: 56,
  H6: 46,
  H7: 37,
  H8: 0,
} as const;

const CURRENT_ORDINARY = {
  O1: 56,
  O2: 46,
  O3: 37,
  O4: 28,
  O5: 20,
  O6: 12,
  O7: 0,
  O8: 0,
} as const;

/** 1992–2016 grades on the new scale: [Higher, Ordinary], from CAO's conversion table. */
const LEGACY: Record<string, readonly [number, number]> = {
  A1: [100, 56],
  A2: [88, 46],
  B1: [88, 46],
  B2: [77, 37],
  B3: [77, 37],
  C1: [66, 28],
  C2: [66, 28],
  C3: [56, 20],
  D1: [56, 20],
  D2: [46, 12],
  D3: [46, 12],
  E: [33, 0],
  F: [0, 0],
  NG: [0, 0],
};

/** The first six current Higher grades and the legacy grades A1–D3 earn the bonus. */
const CURRENT_BONUS_GRADES = ["H1", "H2", "H3", "H4", "H5", "H6"];
const LEGACY_BONUS_GRADES = [
  "A1",
  "A2",
  "B1",
  "B2",
  "B3",
  "C1",
  "C2",
  "C3",
  "D1",
  "D2",
  "D3",
];

export const LCVP_POINTS: Record<LcvpGrade, number> = {
  distinction: 66,
  merit: 46,
  pass: 28,
};

export const LCVP_GRADES: readonly LcvpGrade[] = [
  "distinction",
  "merit",
  "pass",
];

/** Grade codes for a scheme and level, best first. */
export function caoGrades(scheme: CaoScheme, level: CaoLevel): string[] {
  if (scheme === "legacy") {
    return Object.keys(LEGACY);
  }
  return Object.keys(level === "higher" ? CURRENT_HIGHER : CURRENT_ORDINARY);
}

/** Points for a grade before any bonus; null if the grade is not on that scale. */
export function caoBasePoints(
  scheme: CaoScheme,
  level: CaoLevel,
  grade: string
): number | null {
  if (scheme === "legacy") {
    const entry = LEGACY[grade];
    return entry ? entry[level === "higher" ? 0 : 1] : null;
  }
  const table: Record<string, number> =
    level === "higher" ? CURRENT_HIGHER : CURRENT_ORDINARY;
  return table[grade] ?? null;
}

/** Whether a Higher Level Mathematics result at this grade earns the 25-point bonus. */
export function earnsMathsBonus(scheme: CaoScheme, grade: string): boolean {
  const eligible =
    scheme === "legacy" ? LEGACY_BONUS_GRADES : CURRENT_BONUS_GRADES;
  return eligible.includes(grade);
}

export interface CaoSubject {
  id: number;
  level: CaoLevel;
  grade: string;
  /** True for the Higher Level Mathematics row. */
  maths: boolean;
}

export interface ScoredSubject {
  id: number;
  base: number;
  bonus: number;
  score: number;
  counted: boolean;
}

export interface CaoResult {
  scored: ScoredSubject[];
  /** Best six scores added, with the Mathematics bonus. */
  total: number;
  /** Best six base scores added, as if there were no bonus. */
  totalWithoutBonus: number;
  /** What the bonus added to the total (0 when Mathematics misses the six). */
  bonusBenefit: number;
  /** Subjects with a score that were available to count. */
  subjectCount: number;
}

function scoreAll(
  scheme: CaoScheme,
  subjects: readonly CaoSubject[],
  lcvp: LcvpGrade | null,
  withBonus: boolean
): ScoredSubject[] {
  const scored: ScoredSubject[] = [];
  for (const subject of subjects) {
    const base = caoBasePoints(scheme, subject.level, subject.grade);
    if (base !== null) {
      const bonus =
        withBonus &&
        subject.maths &&
        subject.level === "higher" &&
        earnsMathsBonus(scheme, subject.grade)
          ? CAO_MATHS_BONUS
          : 0;
      scored.push({
        id: subject.id,
        base,
        bonus,
        score: base + bonus,
        counted: false,
      });
    }
  }
  if (lcvp !== null) {
    const base = LCVP_POINTS[lcvp];
    scored.push({ id: -1, base, bonus: 0, score: base, counted: false });
  }
  // Highest first; the sort is stable, so equal scores keep the order they were entered in.
  const ranked = [...scored].sort((a, b) => b.score - a.score);
  for (const item of ranked.slice(0, CAO_COUNTED_SUBJECTS)) {
    item.counted = true;
  }
  return scored;
}

const sumCounted = (scored: readonly ScoredSubject[]) =>
  scored.reduce((sum, item) => (item.counted ? sum + item.score : sum), 0);

/**
 * Total CAO points: the six highest subject scores from one sitting, with the Mathematics
 * bonus added to the Mathematics score before the six are picked. `lcvp` is the LCVP Link
 * Modules grade, which competes for one of the six places as a subject.
 */
export function caoTotal(
  scheme: CaoScheme,
  subjects: readonly CaoSubject[],
  lcvp: LcvpGrade | null
): CaoResult {
  const scored = scoreAll(scheme, subjects, lcvp, true);
  const total = sumCounted(scored);
  const plain = scoreAll(scheme, subjects, lcvp, false);
  const totalWithoutBonus = sumCounted(plain);
  return {
    scored,
    total,
    totalWithoutBonus,
    bonusBenefit: total - totalWithoutBonus,
    subjectCount: scored.length,
  };
}
